import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { OFERTA_PATH } from '../legal/operator';

export default function RegisterForm({
  teacherUsername,
  parentToken,
  asParent = false,
  childToken,
  childFirstName = '',
  parentName = '',
}) {
  const isInvitedChild = Boolean(childToken);
  const isParent = !isInvitedChild && (Boolean(parentToken) || asParent);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: childFirstName || '',
    contact_channel: 'email',
    age_14_confirmed: false,
    pdn_consent: false,
    oferta_accepted: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isParent && !isInvitedChild && !formData.age_14_confirmed) {
      setError(
        'Регистрация ученика доступна с 14 лет. Если вам меньше, аккаунт создаёт родитель: '
        + 'он регистрируется, даёт согласие и присылает вам ссылку.',
      );
      return;
    }
    if (!isInvitedChild && !formData.pdn_consent) {
      setError('Отметьте отдельное согласие на обработку персональных данных');
      return;
    }
    if (isParent && !formData.oferta_accepted) {
      setError('Чтобы заключить договор, примите договор-оферту');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        contact_channel: formData.contact_channel,
        pdn_consent: formData.pdn_consent,
      };
      if (isInvitedChild) {
        payload.child_token = childToken;
        delete payload.pdn_consent;
      } else if (isParent) {
        payload.oferta_accepted = true;
        payload.as_parent = true;
      } else {
        payload.age_14_confirmed = true;
      }
      if (teacherUsername) {
        payload.teacher = teacherUsername;
      }
      if (parentToken) {
        payload.parent_token = parentToken;
        payload.as_parent = true;
      }
      await register(payload);
      if (isParent && !parentToken) {
        navigate('/parent');
        return;
      }
      if (parentToken) {
        try {
          const children = await api.get('/parent/children/');
          const list = Array.isArray(children.data) ? children.data : [];
          const child = list[0];
          if (child?.id) {
            navigate(`/parent/children/${child.id}/questionnaire?from=invite`);
            return;
          }
        } catch {
          // fall through
        }
        navigate('/parent');
        return;
      }
      navigate('/questionnaire?from=register');
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const messages = Object.entries(data)
          .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
          .join('; ');
        setError(messages);
      } else {
        setError('Ошибка регистрации');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label htmlFor="first_name">Имя</label>
        <input
          id="first_name"
          name="first_name"
          type="text"
          value={formData.first_name}
          onChange={handleChange}
          placeholder="Ваше имя"
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="username">Имя пользователя</label>
        <input
          id="username"
          name="username"
          type="text"
          value={formData.username}
          onChange={handleChange}
          placeholder="Придумайте логин"
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="password">Пароль</label>
        <div style={{ position: 'relative' }}>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder="Минимум 8 символов"
            required
            minLength={8}
            style={{ paddingRight: '2.6rem' }}
          />
          <button
            type="button"
            className="btn btn-outline btn-xs"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
            title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
            style={{
              position: 'absolute',
              right: '0.4rem',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
          </button>
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="contact_channel">Канал связи</label>
        <select
          id="contact_channel"
          name="contact_channel"
          value={formData.contact_channel}
          onChange={handleChange}
        >
          <option value="email">Email</option>
          <option value="max">Макс</option>
          <option value="phone">Телефон</option>
        </select>
      </div>
      {isInvitedChild && (
        <p className="auth-legal-note">
          {parentName ? <>Родитель <strong>{parentName}</strong> уже дал согласие на обработку ваших персональных данных. </> : null}
          Договор тоже заключает родитель. Отдельная галочка согласия здесь не нужна.
        </p>
      )}
      {!isParent && !isInvitedChild && (
        <fieldset className="pdn-consent-block">
          <legend>Возраст</legend>
          <label className="pdn-consent-label">
            <input
              type="checkbox"
              name="age_14_confirmed"
              checked={formData.age_14_confirmed}
              onChange={handleChange}
            />
            <span>Мне уже есть 14 лет</span>
          </label>
          <p className="auth-legal-note" style={{ margin: '0.7rem 0 0' }}>
            Если вам меньше 14 лет, эту форму отправлять нельзя.{' '}
            <Link to="/register?as=parent">Пусть родитель зарегистрируется</Link>
            : он даст согласие и пришлёт вам ссылку.
          </p>
        </fieldset>
      )}
      {!isInvitedChild && (
      <fieldset className="pdn-consent-block">
        <legend>Согласие на обработку персональных данных</legend>
        <label className="pdn-consent-label">
          <input
            type="checkbox"
            name="pdn_consent"
            checked={formData.pdn_consent}
            onChange={handleChange}
          />
          <span>
            Даю отдельное согласие на обработку персональных данных, в том числе
            логина и адреса электронной почты.{' '}
            <Link to="/consent" target="_blank" rel="noopener noreferrer">
              Текст согласия
            </Link>
          </span>
        </label>
      </fieldset>
      )}
      {isParent ? (
        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="oferta_accepted"
              checked={formData.oferta_accepted}
              onChange={handleChange}
            />
            <span>
              Принимаю{' '}
              <Link to={OFERTA_PATH} target="_blank" rel="noopener noreferrer">
                договор-оферту
              </Link>
              {' '}и заключаю договор как родитель ученика
            </span>
          </label>
        </div>
      ) : !isInvitedChild ? (
        <p className="auth-legal-note">
          Договор на обучение заключает родитель по ссылке из профиля, не эта регистрация.
        </p>
      ) : null}
      <p className="auth-legal-note">
        <Link to="/privacy" target="_blank" rel="noopener noreferrer">
          Политика обработки ПДн
        </Link>
      </p>
      <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
        {loading ? 'Регистрация...' : 'Зарегистрироваться'}
      </button>
    </form>
  );
}
